<template>
    <Modal :is-open="showTripModal" :title="modalTitle" @close="closeModal()">
        <template #content>
            <Input
                label="Name"
                name="title"
                id="trip-name"
                placeholder="Name your trip"
                v-model:inputValue="title"
                class="mb-4"
                :errors="tripFormErrors"
                @onFocus="clearError('title')"
            />
            <Input
                id="trip-destination"
                name="country"
                label="Destination"
                placeholder="Select destination"
                :errors="tripFormErrors"
                class="mb-4"
            >
                <template #input>
                    <div class="flex items-center gap-2">
                        <div class="relative grow">
                            <Suspense>
                                <PlacesAutocomplete
                                    :types="['(regions)']"
                                    id="trip-destination"
                                    placeholder="Select destination"
                                    @placeChanged="placeChanged"
                                    @placeCleared="placeCleared"
                                    @onFocus="clearError('country')"
                                    :error="destinationError"
                                    :currentPlace="tripDestination"
                                    :class="{
                                        '!border-red-500': destinationError,
                                    }"
                                />
                            </Suspense>
                        </div>
                        <button
                            v-if="additionalDestinations.length > 0"
                            type="button"
                            class="shrink-0 p-2 text-slate-400 hover:text-slate-600 dark:text-slate-300 focus:outline-none transition-colors duration-150"
                            @click="removeMainDestination"
                        >
                            <Icon icon="close" :width="16" :height="16" />
                        </button>
                    </div>
                </template>
                <template #error>
                    <div
                        class="text-red-500 text-xs flex items-center gap-2 py-1.5"
                        role="alert"
                        v-if="destinationError"
                    >
                        <div class="flex items-center justify-center">
                            <Icon icon="caution" />
                        </div>
                        Please enter a destination
                    </div>
                </template>
            </Input>

            <div class="mb-4" v-if="additionalDestinations.length">
                <div
                    v-for="(destination, index) in additionalDestinations"
                    :key="index"
                    class="mb-3 flex items-center gap-2"
                >
                    <div class="relative grow">
                        <Suspense>
                            <PlacesAutocomplete
                                :types="['(regions)']"
                                placeholder="Select additional destination"
                                class="w-full"
                                :currentPlace="destination"
                                @placeChanged="
                                    (place) =>
                                        additionalPlaceChanged(place, index)
                                "
                                @placeCleared="
                                    () => additionalPlaceCleared(index)
                                "
                            />
                        </Suspense>
                    </div>
                    <button
                        type="button"
                        class="shrink-0 p-2 text-slate-400 hover:text-slate-600 dark:text-slate-300 focus:outline-none transition-colors duration-150"
                        @click="removeDestination(index)"
                    >
                        <Icon icon="close" :width="16" :height="16" />
                    </button>
                </div>
            </div>

            <div class="mb-4 flex justify-start">
                <button
                    type="button"
                    class="flex items-center gap-2 text-sm text-slate-800 dark:text-slate-100 font-medium hover:text-orange-500 transition-colors duration-200"
                    @click="addDestination"
                >
                    <Icon icon="plus" :width="16" :height="16" />
                    Add destination
                </button>
            </div>

            <SafeDateRangePicker
                v-model:start-date="tripsStore.activeTrip.start_date"
                v-model:end-date="tripsStore.activeTrip.end_date"
                :config="{ dateFormat: 'm/d/Y' }"
                class="mb-4"
            >
                <template #default="{ openMenu, startValue, endValue }">
                    <div class="w-full flex gap-4">
                        <Input
                            id="check-in-date"
                            name="start_date"
                            label="Dates"
                            placeholder="Check In"
                            :options="{
                                icon: 'calendar',
                                iconPosition: 'right',
                                wrapperClass: 'flex-1',
                            }"
                            :errors="tripFormErrors"
                        >
                            <template #input>
                                <input
                                    :value="startValue"
                                    readonly
                                    @click="openMenu"
                                    @focus="clearError('start_date')"
                                    name="start_date"
                                    id="start-date"
                                    class="relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pr-10 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 dark:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm cursor-pointer"
                                    placeholder="Start"
                                    :class="{
                                        '!border-red-500':
                                            tripFormErrors.start_date,
                                    }"
                                />
                            </template>
                        </Input>
                        <Input
                            id="check-out-date"
                            name="end_date"
                            label="End date"
                            placeholder="Check In"
                            :options="{
                                icon: 'calendar',
                                hideLabel: true,
                                iconPosition: 'right',
                                fakeLabelSpace: true,
                                wrapperClass: 'flex-1',
                            }"
                            :errors="tripFormErrors"
                        >
                            <template #input>
                                <input
                                    :value="endValue"
                                    readonly
                                    @click="openMenu"
                                    @focus="clearError('end_date')"
                                    name="end_date"
                                    id="end-date"
                                    class="relative block w-full rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-1.5 pr-10 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 dark:text-slate-400 focus:z-10 focus:ring-0 focus:border-slate-800 hover:border-slate-800 placeholder:text-sm cursor-pointer"
                                    placeholder="End"
                                    :class="{
                                        '!border-red-500':
                                            tripFormErrors.end_date,
                                    }"
                                />
                            </template>
                        </Input>
                    </div>
                </template>
            </SafeDateRangePicker>
            <Textarea
                label="Description"
                name="description"
                id="trip-description"
                class="mb-4"
                placeholder="Write a short description"
                v-model:inputValue="description"
                :errors="tripFormErrors"
                :options="{
                    optional: true,
                }"
                @onFocus="clearError('description')"
            />
            <Input
                label="Cover"
                name="cover"
                id="trip-cover"
                placeholder="Upload or select cover image"
                class="mb-4"
                :errors="tripFormErrors"
                @onFocus="clearError('country')"
            >
                <template #input>
                    <TripImageUpload
                        v-bind:query="tripDestination.valueForSearch"
                        :image="tripImage"
                        :trip="activeTrip"
                        @save="updateCoverImage"
                    />
                </template>
            </Input>
        </template>
        <!-- Removed errors template to prevent duplicate error display -->
        <template #footer>
            <div class="flex justify-end items-center">
                <div class="flex gap-4">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        @click="closeModal"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        id="hs-redemption-create-trip"
                        class="btn btn-primary"
                        @click="handleTripAction"
                    >
                        {{ btnAction }}
                        <span class="inline ml-2" v-if="tripsStore.loading">
                            <i class="fas fa-spinner-third fa-spin"></i>
                        </span>
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useTripsStore } from '@/store/trips';
import { useAppStore } from '@/store/app';
import SafeDateRangePicker from '@/components/ui/SafeDateRangePicker.vue';
import Modal from '@/components/ui/modal/ui-modal.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import Textarea from '@/components/ui/inputs/ui-textarea.vue';
import PlacesAutocomplete from '@/components/ui/places-autocomplete/places-autocomplete.vue';
import TripImageUpload from '@/components/trips/trip-image-upload.vue';
import Icon from '@/components/ui/icon/icon.vue';

const tripsStore = useTripsStore();
const { showTripModal, activeTrip, tripFormErrors } = storeToRefs(tripsStore);

const modalTitle = computed(() => {
    return tripsStore.activeTrip.id ? 'Edit trip' : 'Create new trip';
});

const btnAction = computed(() => {
    return tripsStore.activeTrip.id ? 'Save' : 'Create';
});

const title = computed({
    get() {
        return activeTrip.value.title;
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, title: value });
    },
});

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const tripImage = computed(() => {
    if (!activeTrip.value?.image) return null;
    if (
        typeof activeTrip.value.image === 'string' &&
        activeTrip.value.image.startsWith('http')
    ) {
        return activeTrip.value.image;
    }
    return `${cdnUrl.value}/${activeTrip.value.image.path}/${activeTrip.value.image.file_name}`;
});

const description = computed({
    get() {
        return activeTrip.value.description;
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, description: value });
    },
});

const tripDestination = computed({
    get() {
        const city = activeTrip.value.city || '';
        let state = activeTrip.value.state || '';
        if (state) {
            state = ' ' + state;
        }
        const country = activeTrip.value.country_short
            ? activeTrip.value.country_short
            : activeTrip.value.country || '';

        let formattedAddress = '';
        if ((state || city) && country) {
            formattedAddress = `${city}${state}, ${country}`;
        } else if (state || city) {
            formattedAddress = state || city;
        } else if (!state && !city) {
            formattedAddress = activeTrip.value.country;
        }

        return {
            formattedAddress,
            city,
            state,
            country,
            valueForSearch:
                `${city?.trim()}+${state?.trim()}+${country?.trim()}`
                    .replace(/,/g, '')
                    .replace(/ /g, '+')
                    .replace(/^\+/, '')
                    .replace(/\+$/, '')
                    .replace(/\+\+/, '+')
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, ''),
        };
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, destination: value });
    },
});

const destinationError = ref(false);

const startDateError = computed(() => {
    return tripFormErrors.hasOwnProperty('start_date') &&
        tripFormErrors.value.start_date
        ? true
        : false;
});

const endDateError = computed(() => {
    return tripFormErrors.hasOwnProperty('end_date') &&
        tripFormErrors.value.end_date
        ? true
        : false;
});

const hasSelectedValidPlace = ref(false);

const additionalDestinations = ref([]);

const MAX_DESTINATIONS = 20;

const addDestination = () => {
    const totalDestinations = additionalDestinations.value.length + 1;

    if (totalDestinations >= MAX_DESTINATIONS) {
        notify({
            group: 'error',
            title: 'Maximum destinations reached',
            text: `You can only add up to ${MAX_DESTINATIONS} destinations per trip`,
        });
        return;
    }

    additionalDestinations.value.push(null);
};

const removeDestination = (index) => {
    additionalDestinations.value.splice(index, 1);
};

const additionalPlaceChanged = (place, index) => {
    additionalDestinations.value[index] = place;
};

const additionalPlaceCleared = (index) => {
    additionalDestinations.value[index] = null;
};

const removeMainDestination = () => {
    placeCleared();
    additionalDestinations.value = [];
};

const validate = () => {
    tripsStore.clearErrors();

    if (!activeTrip.value.title) {
        tripsStore.setFormError('title', 'Please enter a trip name');
    }

    if (!activeTrip.value.country) {
        tripsStore.setFormError('country', 'Please select a destination');
    } else if (!hasSelectedValidPlace.value) {
        tripsStore.setFormError(
            'country',
            'Please select a destination from the dropdown options'
        );
    }

    if (!activeTrip.value.start_date) {
        tripsStore.setFormError('start_date', 'Please enter a start date');
    }

    if (!activeTrip.value.end_date) {
        tripsStore.setFormError('end_date', 'Please enter an end date');
    }

    if (
        !tripDestination.value.hasOwnProperty('country') ||
        !tripDestination.value.country
    ) {
        destinationError.value = true;
        tripsStore.setFormError('destination', 'Please enter a destination');
    }
};

const compileDestinations = () => {
    const structuredDestinations = [];

    if (activeTrip.value.city || activeTrip.value.country) {
        const city = (activeTrip.value.city || '').trim();
        const state = (activeTrip.value.state || '').trim();
        const country = (activeTrip.value.country || '').trim();

        let formattedAddress = '';
        if (city && country) {
            formattedAddress = state
                ? `${city}, ${state}, ${country}`
                : `${city}, ${country}`;
        } else if (city) {
            formattedAddress = state ? `${city}, ${state}` : city;
        } else if (country) {
            formattedAddress = country;
        }

        const mainDestObj = {
            city,
            state,
            country,
            latitude: activeTrip.value.latitude || null,
            longitude: activeTrip.value.longitude || null,
            formatted_address: formattedAddress,
        };

        if (
            mainDestObj.city ||
            mainDestObj.country ||
            mainDestObj.formatted_address
        ) {
            structuredDestinations.push(mainDestObj);
        }
    }

    for (const dest of additionalDestinations.value) {
        if (dest?.formattedAddress) {
            let city = '';
            let state = '';
            let country = '';

            if (dest.addressTypes) {
                if (dest.addressTypes.locality) {
                    city =
                        dest.addressTypes.locality.longName ||
                        dest.addressTypes.locality.shortName ||
                        '';
                }

                if (dest.addressTypes.administrative_area_level_1) {
                    state =
                        dest.addressTypes.administrative_area_level_1
                            .longName ||
                        dest.addressTypes.administrative_area_level_1
                            .shortName ||
                        '';
                }

                if (dest.addressTypes.country) {
                    country =
                        dest.addressTypes.country.longName ||
                        dest.addressTypes.country.shortName ||
                        '';
                }
            }

            const destObj = {
                city: city.trim(),
                state: state.trim(),
                country: country.trim(),
                latitude: dest.lat || null,
                longitude: dest.lng || null,
                formatted_address: dest.formattedAddress.trim(),
            };

            if (destObj.city || destObj.country || destObj.formatted_address) {
                structuredDestinations.push(destObj);
            }
        }
    }

    if (structuredDestinations.length > 0) {
        activeTrip.value.destinations = structuredDestinations;
    }
};

const handleTripAction = () => {
    validate();

    // Check if there are any form errors instead of general errors
    const hasFormErrors = Object.keys(tripFormErrors.value).length > 0;

    if (!hasFormErrors) {
        compileDestinations();
        const isCreating = !tripsStore.activeTrip.id;
        const action = isCreating
            ? tripsStore.createTrip()
            : tripsStore.updateTrip();

        action.then((response) => {
            if (isCreating && window._hsq) {
                const appStore = useAppStore();
                const loggedInUser = appStore.userSettings;
                let userFirstName = null;
                let userLastName = null;

                if (loggedInUser && loggedInUser.name) {
                    const nameParts = loggedInUser.name.split(' ');
                    userFirstName = nameParts[0];
                    if (nameParts.length > 1) {
                        userLastName = nameParts.slice(1).join(' ');
                    }
                }

                const newTripData = response.data;

                const eventProperties = {
                    email: loggedInUser ? loggedInUser.email : null,
                    firstname: userFirstName,
                    lastname: userLastName,
                    my10x_user_uuid: loggedInUser ? loggedInUser.uuid : null,
                    trip_id_in_system: newTripData
                        ? newTripData.uuid || newTripData.id
                        : null,
                    trip_name: newTripData ? newTripData.title : null,
                    trip_destination_country: newTripData
                        ? newTripData.country
                        : null,
                    trip_destination_city: newTripData
                        ? newTripData.city
                        : null,
                    trip_destination_state: newTripData
                        ? newTripData.state
                        : null,
                    trip_start_date:
                        newTripData && newTripData.start_date
                            ? new Date(newTripData.start_date)
                                  .toISOString()
                                  .split('T')[0]
                            : null,
                    trip_end_date:
                        newTripData && newTripData.end_date
                            ? new Date(newTripData.end_date)
                                  .toISOString()
                                  .split('T')[0]
                            : null,
                    trip_description_provided:
                        newTripData &&
                        newTripData.description &&
                        newTripData.description.trim() !== ''
                            ? true
                            : false,
                    trip_cover_image_provided:
                        newTripData && newTripData.image ? true : false,
                };

                for (const key in eventProperties) {
                    if (
                        eventProperties[key] === null ||
                        eventProperties[key] === undefined
                    ) {
                        delete eventProperties[key];
                    }
                }

                window._hsq.push([
                    'trackCustomBehavioralEvent',
                    {
                        name: 'pe22692776_complete_adding_a_trip',
                        properties: eventProperties,
                    },
                ]);
            }
            closeModal();
        });
    }
};

const placeChanged = (data) => {
    if (data) {
        let city = '';
        let state = '';
        let country = '';

        if (data.addressTypes) {
            if (data.addressTypes.locality) {
                city =
                    data.addressTypes.locality.longName ||
                    data.addressTypes.locality.shortName ||
                    '';
            }
            if (data.addressTypes.administrative_area_level_1) {
                state =
                    data.addressTypes.administrative_area_level_1.longName ||
                    data.addressTypes.administrative_area_level_1.shortName ||
                    '';
            }
            if (data.addressTypes.country) {
                country =
                    data.addressTypes.country.longName ||
                    data.addressTypes.country.shortName ||
                    '';
            }
        } else {
            city = data.city || '';
            state = data.state || '';
            country = data.country || '';
        }

        tripsStore.setActiveTrip({
            ...activeTrip.value,
            city,
            state,
            country,
            latitude: data.lat || data.latitude || activeTrip.value.latitude,
            longitude: data.lng || data.longitude || activeTrip.value.longitude,
        });
    }

    tripDestination.value = data;
    tripsStore.setPlace(data);
    hasSelectedValidPlace.value = true;
};

const placeCleared = () => {
    tripDestination.value = null;
    tripsStore.clearPlace();
    hasSelectedValidPlace.value = false;
};

const closeModal = () => {
    tripsStore.setShowTripModal(false);
    tripsStore.clearErrors();
    if (activeTrip.value.uuid) tripsStore.getTrip(activeTrip.value.uuid);
};

const updateCoverImage = (imageUrl) => {
    tripsStore.activeTrip.image = imageUrl;
};

const splitDestinations = () => {
    additionalDestinations.value = [];

    if (
        activeTrip.value?.destinations &&
        Array.isArray(activeTrip.value.destinations) &&
        activeTrip.value.destinations.length > 0
    ) {
        const structuredDestinations = activeTrip.value.destinations;

        if (structuredDestinations[0]) {
            const mainDest = structuredDestinations[0];

            tripsStore.setActiveTrip({
                ...activeTrip.value,
                city: mainDest.city || '',
                state: mainDest.state || '',
                country: mainDest.country || '',
                latitude: mainDest.latitude || null,
                longitude: mainDest.longitude || null,
            });

            hasSelectedValidPlace.value = true;

            if (structuredDestinations.length > 1) {
                additionalDestinations.value = structuredDestinations
                    .slice(1)
                    .map((dest) => ({
                        formattedAddress: dest.formatted_address || '',
                        city: dest.city || '',
                        state: dest.state || '',
                        country: dest.country || '',
                        latitude: dest.latitude,
                        longitude: dest.longitude,
                    }));
            }
        }
    } else if (activeTrip.value?.country) {
        hasSelectedValidPlace.value = true;

        const destinations =
            activeTrip.value?.additional_destinations ||
            (activeTrip.value?.destination
                ? activeTrip.value.destination.split(',').map((d) => d.trim())
                : []);

        if (destinations.length > 1) {
            additionalDestinations.value = destinations
                .slice(1)
                .map((dest) => ({
                    formattedAddress: dest,
                }));
        }
    }
};

watch(
    () => showTripModal.value,
    (newValue) => {
        if (!newValue) {
            tripsStore.clearErrors();
            additionalDestinations.value = [];
        } else {
            hasSelectedValidPlace.value =
                activeTrip.value.id && activeTrip.value.country ? true : false;
            splitDestinations();
        }
    }
);

watch(
    () => activeTrip.value?.destinations,
    (newDestinations) => {
        if (
            newDestinations &&
            Array.isArray(newDestinations) &&
            newDestinations.length > 1
        ) {
            additionalDestinations.value = newDestinations
                .slice(1)
                .map((dest) => ({
                    formattedAddress: dest.formatted_address || '',
                    city: dest.city || '',
                    state: dest.state || '',
                    country: dest.country || '',
                    lat: dest.latitude,
                    lng: dest.longitude,
                    addressTypes: {
                        locality: {
                            longName: dest.city || '',
                            shortName: dest.city || '',
                        },
                        administrative_area_level_1: {
                            longName: dest.state || '',
                            shortName: dest.state || '',
                        },
                        country: {
                            longName: dest.country || '',
                            shortName: dest.country_short || '',
                        },
                    },
                }));
        }
    },
    { deep: true }
);

const clearError = (field) => {
    destinationError.value = false;
    if (tripFormErrors.value[field]) {
        delete tripFormErrors.value[field];
    }
};
</script>
